import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { Settings } from "../../../settings";
import { selectBet, selectFreeRoundsActive, selectFreeRoundsBet } from "../../../State/selectors";
import OverlaySymbol from "../OverlaySymbol/OverlaySymbol";
import OverlayReelController from "./OverlayReelController";
import { safeInit } from "../../../utilities";
import { FEATURES_CONFIG } from "../../../featuresConfig";

const OverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, overlaySymbols }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const overlaySymbolRefs = useRef(new Array(Settings.SYMBOLS_VISIBLE));
    const reelContainerRef = useRef(null);
    
    // Use free rounds bet when active, otherwise use regular bet - memoized
    const freeRoundsActive = useSelector(selectFreeRoundsActive);
    const freeRoundsBet = useSelector(selectFreeRoundsBet);
    const regularBet = useSelector(selectBet);
    const bet = useMemo(
      () =>
        FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && freeRoundsBet
          ? freeRoundsBet
          : regularBet,
      [freeRoundsActive, freeRoundsBet, regularBet]
    );

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - (Settings.REEL_COUNT - 1) / 2),
      [reelSpacing, index]
    );

    // ✅ Initialize controller once when component mounts
    useEffect(() => {
      if (!overlayReelControllerRef.current && reelContainerRef.current) {
        safeInit(() => {
          overlayReelControllerRef.current = new OverlayReelController(
            overlaySymbolRefs.current,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current,
            bet
          );
        });
      }

      return () => {
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Handle resize of all overlay symbols
    useEffect(() => {
      if (overlayReelControllerRef.current) {
        overlayReelControllerRef.current.resizeAnimations(
          symbolWidth,
          symbolHeight
        );
      }
    }, [symbolHeight, symbolWidth]);

    // 💰 Bet update - update coin values when bet changes
    useEffect(() => {
      if (!overlayReelControllerRef.current) return;
      overlayReelControllerRef.current.updateBet(bet);
    }, [bet]);

    // ✅ Expose all imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getOverlayReelController: () => overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showAppearAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimation(
          symbolIndex,
          symbolId
        );
      },

      showAppearAnimationAtPosition: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimationAtPosition(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      replaceVisibleSymbols: (targetSymbolId) => {
        overlayReelControllerRef.current?.replaceVisibleSymbols(targetSymbolId);
      },

      playExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReel();
      },

      playExpandingWildFullReelWin: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReelWin();
      },

      hideExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.hideExpandingWildFullReel();
      },

      forceStopExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.forceStopExpandingWildFullReel();
      },

      playExpandingWildDarkAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildDarkAnimation();
      },

      playExpandingWildWinAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildWinAnimation();
      },
    }));

    return (
      <Container
        ref={reelContainerRef}
        name={`spine_overlay_reel_${index}`}
        x={containerX}
      >
        {overlaySymbols.map((_, idx) => (
          <OverlaySymbol
            key={idx}
            index={idx}
            y={symbolHeight * idx}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              overlaySymbolRefs.current[idx] = el;
            }}
          />
        ))}
      </Container>
    );
  }
);

export default OverlayReel;
